<template>
  <div class="login-page-wrapper relative min-h-screen overflow-hidden">
    <!-- Loading overlay (background config) -->
    <div v-if="!isBackgroundReady" class="fixed inset-0 bg-black z-50 flex items-center justify-center">
      <div class="flex flex-col items-center gap-4">
        <div class="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
        <p class="text-white/80 text-sm">Carregando...</p>
      </div>
    </div>

    <!-- Solid gradient background -->
    <div class="auth-background" :style="customBackgroundStyle"></div>

    <!-- Subtle overlay for depth -->
    <div class="auth-overlay"></div>

    <!-- Content layer -->
    <div class="login-glass relative z-10 min-h-screen flex flex-col px-6 py-10 sm:py-12 lg:px-0 lg:py-0">

      <!-- TOP: Logo (mobile only) + Theme toggle -->
      <div class="flex items-center justify-between w-full max-w-sm mx-auto lg:max-w-none lg:justify-end lg:px-12 lg:pt-8 lg:pb-4">
        <!-- Logo (visible only on mobile) -->
        <div class="lg:hidden">
          <img
            :src="logoUrl"
            class="h-12 w-auto logo-invert-dark"
            :alt="nomeEmpresa"
            @touchstart="startLogoPress"
            @touchend="endLogoPress"
            @touchcancel="endLogoPress"
          />
        </div>
        
        <div class="flex items-center gap-2">
          <BackgroundCustomizer
            ref="backgroundCustomizer"
            :nomeEmpresa="nomeEmpresa"
            :showControls="showBackgroundCustomizer"
            @background-change="handleBackgroundChange"
            @button-color-change="handleButtonColorChange"
            @form-styling-change="handleFormStyling"
          />
          
          <button
            @click="handleThemeClick"
            class="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          aria-label="Toggle theme"
          :aria-pressed="isDark"
        >
          <svg
            v-if="!isDark"
            class="w-5 h-5 text-yellow-300"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
            />
          </svg>
          <svg
            v-else
            class="w-5 h-5 text-slate-300"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z"
            />
          </svg>
        </button>
        <input
          v-if="showSecretInput"
          v-model="secretInput"
          @input="checkSecretCode"
          ref="secretInput"
          type="text"
          class="fixed -left-full opacity-0 w-0 h-0"
          autocomplete="off"
        />
        </div>
      </div>

      <!-- CENTER -->
      <div 
        class="flex-1 flex flex-col justify-center w-full"
        :class="parentContainerClass"
        :style="parentContainerStyle"
      >
        <div 
          v-if="showTypingWelcome" 
          class="form-container"
          :class="[formContainerClass, childContainerWidthClass]"
          :style="formContainerStyle"
        >
          <!-- Logo com backdrop (desktop only) -->
          <div class="hidden lg:flex justify-center mb-10">
            <div 
              :class="[
                'inline-flex items-center justify-center px-3 py-2 rounded-xl',
                { 'bg-black/10': !isFormStylingActive }
              ]"
            >
              <img
                :src="logoUrl"
                :class="[
                  'h-20 lg:h-24 w-auto',
                  { 'logo-invert-dark': !isFormStylingActive, 'logo-invert': shouldInvertLogo }
                ]"
                :alt="nomeEmpresa"
              />
            </div>
          </div>

          <!-- Mensagens digitando -->
          <div class="space-y-6 mb-8">
            <!-- "Recuperar senha" -->
            <div v-if="!isFormStylingActive || currentFormConfig?.customTexts?.recoverTitle?.enabled !== false" class="flex justify-center">
              <div 
                :class="[
                  'inline-flex px-3 py-1.5 rounded-xl',
                  { 'bg-black/10': !isFormStylingActive }
                ]"
              >
                <h2 class="text-2xl sm:text-3xl font-bold text-white text-center">
                  {{ typedText1 }}<span v-if="typedText1.length < welcomeMessage1.length" class="animate-pulse">|</span>
                </h2>
              </div>
            </div>
            
            <!-- "Digite seus dados..." -->
            <div v-if="showSecondMessage && (!isFormStylingActive || currentFormConfig?.customTexts?.recoverSubtitle?.enabled !== false)" class="flex justify-center">
              <div class="inline-flex px-3 py-1 rounded-lg">
                <p class="text-sm text-white/60 text-center">
                  {{ typedText2 }}<span v-if="typedText2.length < welcomeMessage2.length" class="animate-pulse">|</span>
                </p>
              </div>
            </div>
          </div>

          <!-- Formulário (aparece após mensagens) -->
          <div v-if="showFormContent">
          <!-- Voltar -->
          <button
            @click="$router.push('/Login')"
            class="flex items-center gap-2 text-white/70 hover:text-white transition-colors mb-6"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
            Voltar
          </button>

          <form @submit.prevent="recuperarSenha" class="space-y-4">
                    <!-- Tipo de cadastro -->
                    <div>
                        <label class="block text-sm font-medium mb-2">
                            Tipo de cadastro*
                        </label>
                        <Dropdown
                            v-model="cadastro.selectedTipoCliente"
                            :options="tiposClientes"
                            optionLabel="label"
                            optionValue="value"
                            placeholder="Selecione um tipo"
                            class="w-full"
                        />
                    </div>

                    <!-- Email -->
                    <div>
                        <label class="block text-sm font-medium mb-2">
                            E-mail*
                        </label>
                        <InputText 
                            v-model="cadastro.email"
                            type="email"
                            placeholder="Insira seu e-mail"
                            @blur="handleEmailBlur"
                            autocomplete="off"
                        />
                    </div>

                    <!-- Apelido (condicional) -->
                    <div v-if="configuracaoApelido === 'S' && cadastro.selectedTipoCliente != 1">
                        <label class="block text-sm font-medium mb-2">
                            Apelido*
                        </label>
                        <Dropdown
                            v-model="cadastro.apelido"
                            :options="apelidos"
                            :disabled="isApelidoDisabled || !apelidos || apelidos.length === 0"
                            :placeholder="apelidos && apelidos.length > 0 ? 'Selecione o apelido' : 'Nenhum apelido disponível'"
                            class="w-full"
                            :class="{ 'p-invalid': erro }"
                        />
                        <p v-if="!apelidos || apelidos.length === 0" class="text-xs text-white/50 mt-1">
                            Nenhum apelido disponível
                        </p>
                    </div>

                    <!-- Botão -->
                    <Button 
                        type="submit"
                        label="Recuperar senha"
                        class="w-full btn-dynamic-color"
                    />
                </form>

                <!-- Footer -->
                <div class="mt-6 text-center">
                    <p class="text-sm text-white/60">
                        Já possui uma conta? 
                        <a :href="configuracaoURLEntrar" class="font-medium text-white/80 hover:text-white hover:underline transition-colors">
                            Entrar
                        </a>
                    </p>
                </div>
          </div>
        </div>
      </div>
    </div>

    <ModalAvisoLogin :mensagem="messageError" :mensagensComplementares="mensagensComplementares" />
  </div>
</template>

<script>
import { required, email, minLength } from '@vuelidate/validators';
import validateCpf from '@/utils/validateCpf';
import validateCnpj from '@/utils/validateCnpj';
import { mapGetters, mapActions } from 'vuex';
import api from '@/api/axios';
import ModalAvisoLogin from '@/views/Login/ModalAvisoLogin.vue';
import BackgroundCustomizer from '@/components/BackgroundCustomizer.vue';
import { useTheme } from '@/composables/useTheme';
import { useEditorMode } from '@/composables/useEditorMode';

export default {
    name: 'RecuperarSenha',
    components: {
        ModalAvisoLogin,
        BackgroundCustomizer,
    },
    setup() {
        const { isDark, toggleTheme } = useTheme();
        const { editorModeActive, toggleEditorMode, initEditorMode } = useEditorMode();
        
        initEditorMode();
        
        return {
            isDark,
            toggleTheme,
            editorModeActive,
            toggleEditorMode,
        };
    },
    data() {
        return {
            nomeEmpresa: '',
            configuracaoApelido: '',
            configuracaoTiposClientes: {},
            tiposClientes: [],
            messageError: null,
            isApelidoDisabled: false,
            apelidos: null,
            cadastro: {
                email: null,
                selectedTipoCliente: null,
                apelido: null
            },
            permitirAvancar: false,
            configuracaoURLEntrar: '/Login',
            // Typing animation
            showTypingWelcome: false,
            typedText1: "",
            typedText2: "",
            welcomeMessage1: "",
            welcomeMessage2: "",
            showSecondMessage: false,
            showFormContent: false,
            customBackgroundStyle: null,
            customButtonColor: null,
            isBackgroundReady: false,
            formStylingConfig: null,
            // Mobile logo press to reveal customizer
            showBackgroundCustomizer: false,
            logoPressTimer: null,
            // Secret editor mode
            themeClickCount: 0,
            themeClickTimer: null,
            secretInput: '',
            showSecretInput: false,
        };
    },
    computed: {
        ...mapGetters({
            account: 'GET_CREATE_ACCOUNT',
            getConfiguracoes: 'GET_CONFIGURACOES',
        }),
        logoUrl() {
            return require('@/' + process.env.VUE_APP_URL_LOGO);
        },
        detectedDevice() {
            const width = window.innerWidth;
            if (width < 768) return 'mobile';
            if (width < 1024) return 'tablet';
            return 'desktop';
        },
        isFormStylingActive() {
            return this.formStylingConfig?.enabled?.[this.detectedDevice] || false;
        },
        currentFormConfig() {
            if (!this.isFormStylingActive) return null;
            return this.formStylingConfig?.[this.detectedDevice] || null;
        },
        formContainerStyle() {
            if (!this.isFormStylingActive || !this.currentFormConfig) return {};
            const { backgroundColor, opacity, width, minWidth, maxWidth, margin } = this.currentFormConfig;
            return {
                backgroundColor: this.hexToRgba(backgroundColor, opacity / 100),
                backdropFilter: 'blur(16px)',
                borderRadius: '1rem',
                padding: '2rem',
                width: width || '100%',
                minWidth: minWidth || '0px',
                maxWidth: maxWidth || '600px',
                marginLeft: margin || '0rem',
                marginRight: margin || '0rem'
            };
        },
        formContainerClass() {
            if (!this.isFormStylingActive || !this.currentFormConfig) return '';
            const { alignment } = this.currentFormConfig;
            return alignment === 'left' ? 'mr-auto' : 
                   alignment === 'right' ? 'ml-auto' : 'mx-auto';
        },
        parentContainerClass() {
            if (!this.isFormStylingActive || !this.currentFormConfig) {
                return 'items-center mx-auto';
            }
            const { alignment } = this.currentFormConfig;
            if (alignment === 'left') {
                return 'items-start';
            } else if (alignment === 'right') {
                return 'items-end';
            } else {
                return 'items-center mx-auto';
            }
        },
        parentContainerStyle() {
            if (!this.isFormStylingActive || !this.currentFormConfig) {
                return {};
            }
            const { alignment, alignmentOffset } = this.currentFormConfig;
            const style = {};
            
            if (alignment === 'left') {
                style.paddingLeft = `${alignmentOffset * 0.25}rem`;
            } else if (alignment === 'right') {
                style.paddingRight = `${alignmentOffset * 0.25}rem`;
            }
            
            return style;
        },
        childContainerWidthClass() {
            if (!this.isFormStylingActive || !this.currentFormConfig) {
                return 'w-full';
            }
            return '';
        },
        shouldInvertLogo() {
            if (!this.isFormStylingActive || !this.currentFormConfig) {
                return false;
            }
            return this.currentFormConfig.logoInvert;
        },
        customRecoverTitle() {
            if (this.isFormStylingActive && this.currentFormConfig?.customTexts?.recoverTitle) {
                const value = this.currentFormConfig.customTexts.recoverTitle;
                if (typeof value === 'string') return value;
                if (value.enabled && value.content) return value.content;
            }
            return 'Recuperar senha';
        },
        customRecoverSubtitle() {
            if (this.isFormStylingActive && this.currentFormConfig?.customTexts?.recoverSubtitle?.enabled) {
                const value = this.currentFormConfig.customTexts.recoverSubtitle;
                if (typeof value === 'string') return value;
                if (value.enabled && value.content) return value.content;
            }
            return 'Digite seus dados para recuperar o acesso.';
        },
        dynamicTextStyles() {
            if (!this.isFormStylingActive || !this.currentFormConfig) return '';
            const config = this.currentFormConfig;
            return `
                .form-container h1, .form-container h2, .form-container h3,
                .form-container h4, .form-container h5, .form-container h6,
                .form-container span, .form-container label, .form-container p {
                    color: ${config.textColor} !important;
                }
                .form-container input:enabled, .form-container select:enabled, .form-container textarea:enabled {
                    background-color: ${config.inputBgEnabled} !important;
                    color: ${config.inputTextColor} !important;
                }
                .form-container input:disabled, .form-container select:disabled, .form-container textarea:disabled {
                    background-color: ${config.inputBgDisabled} !important;
                }
                .login-glass .form-container label,
                .login-glass .form-container p {
                    color: ${config.textColor} !important;
                    background: none !important;
                }
                .login-glass .form-container .p-inputtext:enabled,
                .login-glass .form-container input.p-inputtext:enabled,
                .login-glass .form-container input.p-component.p-inputtext:enabled,
                .login-glass .form-container .p-password input.p-inputtext:enabled,
                .login-glass .form-container .p-password input.p-component.p-inputtext:enabled,
                .login-glass .form-container .p-dropdown:enabled,
                .login-glass .form-container select:enabled,
                .login-glass .form-container textarea:enabled {
                    background: ${config.inputBgEnabled} !important;
                    color: ${config.inputTextColor} !important;
                }
                .login-glass .form-container .p-inputtext:disabled,
                .login-glass .form-container input.p-inputtext:disabled,
                .login-glass .form-container input.p-component.p-inputtext:disabled,
                .login-glass .form-container .p-password input.p-inputtext:disabled,
                .login-glass .form-container .p-password input.p-component.p-inputtext:disabled,
                .login-glass .form-container .p-dropdown:disabled,
                .login-glass .form-container select:disabled,
                .login-glass .form-container textarea:disabled {
                    background: ${config.inputBgDisabled} !important;
                }
                .login-glass .form-container .p-inputtext,
                .login-glass .form-container input.p-inputtext,
                .login-glass .form-container .p-password input {
                    color: ${config.inputTextColor} !important;
                }
                .login-glass .form-container .p-inputtext::placeholder,
                .login-glass .form-container input.p-inputtext::placeholder,
                .login-glass .form-container .p-password input::placeholder {
                    color: ${config.inputTextColor} !important;
                    opacity: 0.6 !important;
                }
                .form-container a,
                .form-container ~ div a,
                .form-container ~ .text-center a {
                    color: ${config.textColor} !important;
                }
                .form-container button:not([type="submit"]),
                .form-container ~ button:not([type="submit"]),
                button.flex.items-center {
                    color: ${config.textColor} !important;
                }
                .form-container button[type="submit"],
                .form-container button.w-full,
                .form-container button.btn-dynamic-color {
                    color: ${config.buttonTextColor} !important;
                }
                .form-container button[type="submit"],
                .form-container button.w-full:not(.border),
                .form-container button.btn-dynamic-color,
                .login-glass .p-button:not(.p-button-text):not(.p-button-outlined),
                .login-glass .form-container .p-button:not(.p-button-text):not(.p-button-outlined) {
                    background-color: ${this.hexToRgba(config.buttonColorType1, config.buttonOpacityType1 / 100)} !important;
                    border-color: ${this.hexToRgba(config.buttonColorType1, config.buttonOpacityType1 / 100)} !important;
                }
                .form-container button[type="submit"]:hover,
                .form-container button.w-full:not(.border):hover,
                .form-container button.btn-dynamic-color:hover,
                .login-glass .p-button:not(.p-button-text):not(.p-button-outlined):hover,
                .login-glass .form-container .p-button:not(.p-button-text):not(.p-button-outlined):hover {
                    background-color: ${config.buttonHoverColor} !important;
                    border-color: ${config.buttonHoverColor} !important;
                }
                button.w-full.border,
                button.h-12.border {
                    background-color: ${this.hexToRgba(config.buttonColorType2, config.buttonOpacityType2 / 100)} !important;
                    border-color: ${config.buttonColorType2} !important;
                }
            `;
        },
    },
    validations() {
        const validations = {
            email: { required, email },
        };
        if (this.cadastro.selectedTipoCliente === 'PF') {
            validations.CPF = {
                required,
                validateCpf
            };
            validations.nomeCompleto = {
                required,
                minLength: minLength(5) // Supondo que o nome completo deve ter pelo menos 5 caracteres
            };
        } else if (this.cadastro.selectedTipoCliente === 'PJ') {
            validations.CNPJ = {
                required,
                validateCnpj
            };
            validations.razaoSocial = {
                required,
                minLength: minLength(5) // Supondo que o nome completo deve ter pelo menos 5 caracteres
            };
        }
        return { cadastro: validations };
    },
    mounted() {
        this.nomeEmpresa = process.env.VUE_APP_NOME_EMPRESA;
        this.welcomeMessage1 = this.customRecoverTitle;
        this.welcomeMessage2 = this.customRecoverSubtitle;
        this.showTypingWelcome = true;
        this.startTypingAnimation();
    },
    async created() {
        await this.fetchConfiguracao(['Redirecionamento do botão entrar Onboarding externo', 'Exibir apelido', 'Remover tipo de cadastro']);
        this.configuracaoTiposClientes = this.getConfiguracoes('Remover tipo de cadastro');
        this.configuracaoApelido = this.getConfiguracoes('Exibir apelido').Valor;
        this.configuracaoURLEntrar = this.getConfiguracoes('Redirecionamento do botão entrar Onboarding externo')?.Valor ?? '/Login';

    },
    methods: {
        ...mapActions({
            fetchConfiguracao: 'FETCH_CONFIGURACOES'
        }),
        hexToRgba(hex, alpha) {
            const r = parseInt(hex.slice(1, 3), 16);
            const g = parseInt(hex.slice(3, 5), 16);
            const b = parseInt(hex.slice(5, 7), 16);
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        },
        handleFormStyling(config) {
            this.formStylingConfig = config;
        },
        handleBackgroundChange(style) {
            this.customBackgroundStyle = style;
            this.isBackgroundReady = true;
            console.log('✅ Background aplicado e pronto para exibição');
        },
        handleButtonColorChange(color) {
            this.customButtonColor = color;
        },
        handleThemeClick() {
            this.toggleTheme();
            
            // Sincroniza o modo interno do form styling com o tema visual
            if (this.$refs.backgroundCustomizer) {
                const newMode = this.isDark ? 'dark' : 'light';
                console.log(`🔄 Sincronizando form styling para modo ${newMode} (tema visual alterado)`);
                this.$refs.backgroundCustomizer.syncModeWithTheme(newMode);
            }
            
            this.themeClickCount++;
            
            if (this.themeClickTimer) {
                clearTimeout(this.themeClickTimer);
            }
            
            this.themeClickTimer = setTimeout(() => {
                this.themeClickCount = 0;
            }, 2000);
            
            if (this.themeClickCount === 3) {
                this.showSecretInput = true;
                this.secretInput = '';
                this.themeClickCount = 0;
                
                this.$nextTick(() => {
                    if (this.$refs.secretInput) {
                        this.$refs.secretInput.focus();
                    }
                });
                
                setTimeout(() => {
                    if (this.secretInput === '') {
                        this.showSecretInput = false;
                    }
                }, 5000);
            }
        },
        checkSecretCode() {
            const input = this.secretInput.toLowerCase();
            
            if (input === 'editor') {
                this.toggleEditorMode();
                this.showBackgroundCustomizer = this.editorModeActive;
                this.showSecretInput = false;
                this.secretInput = '';
                
                const msg = this.editorModeActive ? 'Editor mode activated' : 'Editor mode deactivated';
                console.log(`🎨 ${msg}`);
            }
            
            if (input.length > 6 && input !== 'editor') {
                this.showSecretInput = false;
                this.secretInput = '';
            }
        },
        startLogoPress() {
            // Apenas no mobile e para empresas DNWAYS
            if (window.innerWidth >= 1024) {
                // Desktop - sempre mostra
                this.showBackgroundCustomizer = true;
                return;
            }
            
            const isDnways = this.nomeEmpresa === 'DNWAYS' || 
                           this.nomeEmpresa === 'DNWAYS_DEMO_Y' || 
                           this.nomeEmpresa === 'DNWAYS_DEMO_X';
            
            if (!isDnways) return;
            
            // Mobile - requer 5 segundos de press
            this.logoPressTimer = setTimeout(() => {
                this.showBackgroundCustomizer = true;
                console.log('🎨 Background customizer ativado!');
            }, 5000);
        },
        endLogoPress() {
            if (this.logoPressTimer) {
                clearTimeout(this.logoPressTimer);
                this.logoPressTimer = null;
            }
        },
        startTypingAnimation() {
            this.showTypingWelcome = true;

            // Verifica se a animação está desabilitada via env
            const disableTyping = process.env.VUE_APP_DISABLE_TYPING_ANIMATION === 'true';
            
            if (disableTyping) {
                // Mostra tudo imediatamente sem animação
                this.typedText1 = this.welcomeMessage1;
                this.typedText2 = this.welcomeMessage2;
                this.showSecondMessage = true;
                this.showFormContent = true;
                return;
            }

            // Animação normal de digitação
            const typingSpeed = 60;
            const pauseBetweenMessages = 600;
            const pauseBeforeForm = 500;
            
            // Digita primeira mensagem
            let index1 = 0;
            const typeFirstMessage = () => {
                if (index1 < this.welcomeMessage1.length) {
                    this.typedText1 += this.welcomeMessage1[index1];
                    index1++;
                    setTimeout(typeFirstMessage, typingSpeed);
                } else {
                    setTimeout(() => {
                        this.showSecondMessage = true;
                        typeSecondMessage();
                    }, pauseBetweenMessages);
                }
            };
            
            // Digita segunda mensagem
            let index2 = 0;
            const typeSecondMessage = () => {
                if (index2 < this.welcomeMessage2.length) {
                    this.typedText2 += this.welcomeMessage2[index2];
                    index2++;
                    setTimeout(typeSecondMessage, typingSpeed);
                } else {
                    setTimeout(() => {
                        this.showFormContent = true;
                    }, pauseBeforeForm);
                }
            };
            
            setTimeout(typeFirstMessage, 300);
        },
        async handleEmailBlur() {
            if (!this.cadastro.email) {
                this.apelidos = [];
                return;
            }
            this.loading = true;
            try {
                console.log(this.cadastro.selectedTipoCliente);
                if (this.configuracaoApelido == 'S' && this.cadastro.selectedTipoCliente != '1') {
                    const response = await api.get('/login', {
                        params: { chaveLogin: this.cadastro.email, tipoCliente: this.cadastro.selectedTipoCliente },
                    });
                    this.apelidos = response.data.data;
                    if (this.cadastro.email.includes('@')) {
                        this.cadastro.Apelido = null;
                        this.isApelidoDisabled = false;
                    } else {
                        this.cadastro.Apelido = this.apelidos.length > 0 ? this.apelidos[0] : null;
                        this.isApelidoDisabled = true;
                    }
                }
            } catch (error) {
                this.apelidos = null;

                if(error.response.data.descricao){
                    this.messageError = error.response.data.descricao;
                    this.avisoLogin();
                    this.erro = true;
                    this.disableBtnLogin = false;
                }

                console.error('Erro ao buscar apelidos:', error);
            } finally {
                this.loading = false;
            }
        },
        avisoLogin() {
            this.$store.commit('SET_MODAL_AVISO_LOGIN', true)
        },
        limparCadastro() {
            this.cadastro.email = null;
        },
        validarAvancar() {
            var nomeCampo = '';

            if(this.cadastro.selectedTipoCliente == null && nomeCampo == ''){
                nomeCampo = 'Tipo de cadastro';
            }

            if ((this.cadastro.email == '' || this.cadastro.email == null) && nomeCampo == '') {
                nomeCampo = 'E-mail';
            }

            if (this.configuracaoApelido == 'S' && this.cadastro.selectedTipoCliente != '1' && (this.cadastro.apelido == null || this.cadastro.apelido == '') && nomeCampo == '') {
                nomeCampo = 'Apelido';
            }

            if (nomeCampo != '') {
                this.$alert({
                    icon: 'error',
                    title: 'Oops...',
                    text: `Necessário preencher o campo ` + nomeCampo,
                });
                return false;
            }

            return true;
        },
        async recuperarSenha() {
            if (this.validarAvancar()) {
                 
                var objConcluir = { Email: this.cadastro.email, TipoCliente: this.cadastro.selectedTipoCliente, Apelido: this.cadastro.apelido };
                await api.post('/register/restaurar-senha', objConcluir)
                    .then((retorno) => {
                        if (retorno.status == 200) {
                            this.$alert({
                                icon: 'success',
                                title: 'E-mail enviado!',
                                text: `Acesse o e-mail para visualizar sua senha de acesso.`,
                            });
                        }
                    })
                    .catch(() => {
                        this.$alert({
                            icon: 'error',
                            title: 'Conta não localizada',
                            text: 'Nenhuma conta com endereço de e-mail informado foi localizado',
                        });
                    });
            }
        }
    },
    watch: {
        customRecoverTitle(newVal) {
            this.welcomeMessage1 = newVal;
        },
        customRecoverSubtitle(newVal) {
            this.welcomeMessage2 = newVal;
        },
        consultarPermissaoAvancar() {
            this.permitirAvancar = false;
            if (this.cadastro.selectedTipoCliente == 'PF') {
                // Para Pessoa Física, nome completo e CPF são obrigatórios
                if (this.cadastro.nomeCompleto && this.cadastro.nomeCompleto.length > 5 && this.validarCPF(this.cadastro.CPF)) {
                    this.permitirAvancar = true;
                }
            } else if (this.cadastro.selectedTipoCliente == 'PJ' || this.cadastro.selectedTipoCliente == 'FU') {
                // Para Pessoa Jurídica ou Funcionário, razão social e CNPJ são obrigatórios
                if (this.cadastro.razaoSocial && this.cadastro.CNPJ && this.validarCNPJ(this.cadastro.CNPJ)) {
                    this.permitirAvancar = true;
                }
            }
        },
        configuracaoTiposClientes(newVal) {
            if (newVal && newVal.Valor) {
                const valores = newVal.Valor.replace('\r\n', ';').replace(',', ';').split(';').map(item => item.trim()).filter(item => item);
                const todasOpcoes = [
                    { value: '1', label: 'Pessoa Física' },
                    { value: '2', label: 'Pessoa Jurídica' },
                    { value: '3', label: 'Fundo de Investimento' }
                ];
                const tiposFiltrados = todasOpcoes.filter(opcao => !valores.includes(opcao.value));
                if (JSON.stringify(tiposFiltrados) !== JSON.stringify(this.tiposClientes)) {
                    this.tiposClientes = tiposFiltrados;
                }
            } else {
                if (this.tiposClientes.length > 0) {
                    this.tiposClientes = [];
                }
            }
            if (this.tiposClientes.length > 0) {
                this.cadastro.selectedTipoCliente = parseInt(this.tiposClientes[0].value);
            }
        },
    }
}
</script>

<style scoped v-html="dynamicTextStyles"></style>

